import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import i18n from '../../../../i18n';
import { useSelector } from 'react-redux';
import { Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { makeUrl } from 'core/utils/utils';
import { useAction } from 'core/store/store';

import { makeRegistrationSetStageAction } from 'state/registration/actions';
import { RegistrationStage } from 'state/registration/reducer';
import { catalogCategoriesSelector, catalogProductsSelector } from 'state/catalog/selectors';

import { Footer } from 'components/Footer/Footer';
import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';
import { Button, ButtonColor } from 'components/Button/Button';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { LayoutFullHeightContainer } from 'components/Layout/LayoutFullHeightContainer';
import { LayoutFixedItem } from 'components/Layout/LayoutFixedItem';

import { RoutePath } from 'RoutePath';

import { ReactComponent as PlusIcon } from 'assets/icons/main-16/plus--16.svg';
import { DraggableProductList } from 'components/DraggableProductList/DraggableProductList';

const useStyles = makeStyles(theme => ({
    pageWrapper: {
        paddingBottom: 20,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: 0,
        },
    },
    productCard: {
        display: 'flex',
        marginBottom: theme.spacing(3),
    },
    container: {
        height: '100%',
        [theme.breakpoints.up(1280)]: {
            width: 880,
            margin: '0 auto',
        },
        [theme.breakpoints.up(1920)]: {
            width: 1340,
            margin: '0 auto',
        },
    },
    productCardDetails: {
        flex: 1,
        marginLeft: theme.spacing(2),
    },
    description: {
        display: 'block',
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
        maxHeight: theme.spacing(6.5),
        overflow: 'hidden',
    },
    dot: {
        display: 'inline-block',
        textAlign: 'center',
        width: theme.spacing(3),
    },
    footer: {
        flexGrow: 0,
        flexBasis: 'auto',
        marginTop: theme.spacing(2),
        padding: 0,
        paddingBottom: theme.spacing(16),
        borderTop: '1px solid rgba(15, 24, 37, 0.05)',
    },
    addProductButtonWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        position: 'fixed',
        right: 40,
        bottom: 68,
    },
    addProductMobileButton: {
        padding: '12px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        width: '100%',
        bottom: 50,
        marginBottom: 10,
        marginTop: -16,
    },
    addProductMobileButtonIcon: {
        marginRight: 8,
    },
    footerControls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(3),
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        width: '100%',
    },
    footerControlsButton: {
        width: '100%',
        height: 48,
    },
    addProductButton: {
        padding: '12px 16px',
        height: 48,
    },

    addButton: {
        cursor: 'pointer',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.fixedWhite.primary,
        width: theme.spacing(22),
        height: theme.spacing(4),
        borderRadius: theme.spacing(12.5),
    },
}));

export const CatalogList = () => {
    const action = useAction();
    const classes = useStyles();
    const history = useHistory();
    const { t } = i18n.useTranslation();
    const isStoreRegistration = window.location.pathname === RoutePath.REGISTRATION_CATALOG;
    const categories = useSelector(catalogCategoriesSelector);
    const products = useSelector(catalogProductsSelector);
    return (
        <ErrorBoundary>
            <div className={classes.pageWrapper}>
                <LayoutFullHeightContainer>
                    <div className={classes.container}>
                        <DraggableProductList
                            categories={categories}
                            products={products}
                            onProductClick={(productId, categoryId) => {
                                const path = makeUrl(
                                    isStoreRegistration
                                        ? RoutePath.REGISTRATION_CATALOG_EDIT
                                        : RoutePath.VENDOR_CATALOG_EDIT,
                                    { categoryId, productId },
                                );
                                history.push(path);
                            }}
                        />
                    </div>

                    <Hidden xsDown>
                        <div className={classes.addProductButtonWrapper}>
                            <Link
                                to={
                                    isStoreRegistration
                                        ? RoutePath.REGISTRATION_CATALOG_ADD
                                        : RoutePath.VENDOR_CATALOG_ADD
                                }
                            >
                                <Button className={classes.addProductButton} color={ButtonColor.DARK}>
                                    {t('onboarding:emptyCatalog.addButton')}
                                </Button>
                            </Link>
                        </div>
                    </Hidden>

                    <Hidden smUp>
                        <LayoutFixedItem>
                            <div className={classes.addProductMobileButton}>
                                <Link
                                    to={
                                        isStoreRegistration
                                            ? RoutePath.REGISTRATION_CATALOG_ADD
                                            : RoutePath.VENDOR_CATALOG_ADD
                                    }
                                >
                                    <div className={classes.addButton}>
                                        <Icon
                                            className={classes.addProductMobileButtonIcon}
                                            component={PlusIcon}
                                            size={16}
                                            svgSize={16}
                                            color="#243143"
                                        />

                                        <Text typography={TextTypography.PARA_ACCENT}>
                                            {t('onboarding:emptyCatalog.addButton')}
                                        </Text>
                                    </div>
                                </Link>
                            </div>
                            <Footer className={classes.footer}>
                                {isStoreRegistration && (
                                    <div className={classes.footerControls}>
                                        <Button
                                            className={classes.footerControlsButton}
                                            color={ButtonColor.DARK}
                                            onClick={() => {
                                                action(makeRegistrationSetStageAction, RegistrationStage.SETTINGS);
                                            }}
                                        >
                                            {t('onboarding:footer.nextButton')}
                                        </Button>
                                    </div>
                                )}
                            </Footer>
                        </LayoutFixedItem>
                    </Hidden>
                </LayoutFullHeightContainer>
            </div>
        </ErrorBoundary>
    );
};
